import { useCallback, useMemo } from 'react';
import { toast } from '@relay-towing/ui';
import { rememberReturnTo } from '../lib/returnTo';
import { signInNative, type NativeSignIn } from '../lib/nativeBridge';
import { useHybridAuth } from './useHybridAuth';
import '../types/webview';

/**
 * The ways in from a signed-out page. In a v2 wrapper the wrapper signs in,
 * in the phone's own sign-in sheet, and hands the page a token
 * (docs/native-bridge.md); in a browser each one leaves for Auth0, so the
 * page the person opened is saved first and Auth0's return lands back on it.
 */
export function useSignIn() {
  const { loginWithRedirect } = useHybridAuth();

  /**
   * In a wrapper, the wrapper's sign-in, settled once it has signed in or
   * said why not; null in a browser. Closing the sheet is a choice, not an
   * error, so it says nothing.
   */
  const viaWrapper = useCallback((how: NativeSignIn) => {
    const reply = signInNative(how);
    if (!reply) return null;
    return reply.then((result) => {
      if (!result.signedIn && !result.cancelled) {
        toast.error(result.message ?? 'Could not sign in. Try again.');
      }
    });
  }, []);

  const signIn = useCallback(() => {
    const native = viaWrapper({});
    if (native) return native;
    rememberReturnTo();
    return loginWithRedirect();
  }, [viaWrapper, loginWithRedirect]);

  /**
   * Google: Android builds before bridge v2 sign in with Google natively
   * through their v1 global, which the old sign-in screen called; a browser
   * through Auth0's Google connection.
   */
  const signInWithGoogle = useCallback(() => {
    const native = viaWrapper({ connection: 'google-oauth2' });
    if (native) return native;
    rememberReturnTo();
    const legacy = window.ReactNativeWebView;
    if (legacy && typeof legacy.signInWithGoogle === 'function') {
      legacy.signInWithGoogle();
      return;
    }
    return loginWithRedirect({
      authorizationParams: { connection: 'google-oauth2' },
    });
  }, [viaWrapper, loginWithRedirect]);

  /**
   * Apple, offered only inside the iOS wrapper: the App Store asks for it
   * beside Google, and the wrapper signs in with Apple natively.
   */
  const signInWithApple = useCallback(
    () => viaWrapper({ connection: 'apple' }),
    [viaWrapper]
  );

  /**
   * Straight to the password page for a known email: the account sign-up
   * has just made, as the dashboard's wizard ends.
   */
  const signInAs = useCallback(
    (email: string) =>
      viaWrapper({ loginHint: email }) ??
      loginWithRedirect({
        authorizationParams: { login_hint: email, screen_hint: 'login' },
      }),
    [viaWrapper, loginWithRedirect]
  );

  return useMemo(
    () => ({ signIn, signInWithGoogle, signInWithApple, signInAs }),
    [signIn, signInWithGoogle, signInWithApple, signInAs]
  );
}
